import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { filterConnectedCases } from './mapping/filter-connected-cases';
import { mapBowtieData, mapCaseData } from './mapping/map-bowtie';
import { mapIncludeConnectedCases } from './mapping/map-include-connected-cases';
import { getInspectionEvaluations, mapChecklistEvaluation } from './mapping/map-inspection';
import { api } from './api';
import { api as caseApi } from 'app/area/case/features/connected-cases/api';
import {
  ClearBowtieData,
  RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft, CleanData,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases, RequestBowtieFavourite, ReceiveBowtieFavourite, IncrementRequestId,
  RequestConnectedCasesByConnectionType,
  ReceiveConnectedCasesByConnectionType,
  UpdateBowtie,
  RequestChangeConnectionSequence,
  ReceiveChangeConnectionSequence,
  FailChangeConnectionSequence,
  RequestValidateCaseId,
  ReceiveValidateCaseId,
  FailValidateCaseId
} from './action-types';
import { BowtieLayout } from './types';
import { BowtieInstanceState } from 'app/area/bowtie/types-store';
import { clientCache } from 'app/infrastructure/universal';
import { bowtieFavouriteIdCache } from 'app/area/bowtie/constants';
import { groupConnectedCases, groupConnectedCasesByConnectionType } from 'app/area/bowtie/mapping/group-connected-cases';
import { calculateMoveSteps, updateSequence } from 'app/area/case/features/connected-cases/sequence';

function getBowtieInstanceState(getState: () => ApplicationState, instanceKey: string): BowtieInstanceState {
  return getState().bowtie?.instances?.[instanceKey] || {} as BowtieInstanceState;
}

export const actionCreators = {

  loadBowtie: (
    bowtieLayout: BowtieLayout,
    caseNumber: number,
    connectedDraftId: number,
    instanceKey: string,
    retries = 0
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const draftMode = isDefinedIncludingZero(connectedDraftId);
      dispatch(new ClearBowtieData(instanceKey));
      dispatch(new RequestBowtie(caseNumber, connectedDraftId, instanceKey));

      if (draftMode) {
        await dispatch(actionCreators.loadDraft(connectedDraftId, instanceKey));
      }

      try {
        const bowtieFavouritePromise = dispatch(actionCreators.createBowtieFavouriteIfNeeded(instanceKey));

        const layoutData = bowtieLayout != null
          ? bowtieLayout
          : await api.loadLayout();

        const {
          draftData
        } = getBowtieInstanceState(getState, instanceKey);

        const includeConnectedCases = !draftMode || mapIncludeConnectedCases(layoutData);

        const draftOriginalCase = draftMode && isDefinedIncludingZero(draftData?.caseNumber)
          ? draftData.caseNumber : undefined;

        await bowtieFavouritePromise;
        const {
          bowtieFavouriteId
        } = getBowtieInstanceState(getState, instanceKey);

        let bowtieData;
        try {
           bowtieData = await api.loadBowtie(caseNumber, includeConnectedCases, draftOriginalCase, bowtieFavouriteId);
        } catch (err) {
          if (err?.response?.status === 400 && retries < 1) {
            dispatch(actionCreators.clean(instanceKey));
            await dispatch(actionCreators.loadBowtie(bowtieLayout, caseNumber, connectedDraftId, instanceKey, retries + 1));
            return; 
          } else {
            throw err;
          }
        }

        const caseDataModel = mapBowtieData(bowtieData.caseDataModel);
        const evaluations = getInspectionEvaluations(caseDataModel.cases);

        if (caseDataModel.cases.length === 0) {
          dispatch(new FailBowtie(caseNumber, connectedDraftId, instanceKey));
        } else {
          dispatch(new ReceiveBowtie(caseNumber, connectedDraftId, caseDataModel, layoutData,
            bowtieData.caseTemplate, evaluations, instanceKey));
        }

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailBowtie(caseNumber, connectedDraftId, instanceKey));
      }
    },

  loadDraft: (draftId: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const { draftRequestId } = getBowtieInstanceState(getState, instanceKey);
      const requestId = (draftRequestId || 0) + 1;

      dispatch(new RequestDraft(requestId, instanceKey));

      try {
        const [
          draftData,
          connectionData
        ] = await Promise.all([
          api.loadDraft(draftId),
          api.loadDraftConnectionOptions(draftId)
        ]);

        const connectionTypes = connectionData && connectionData.rootCodes &&
          connectionData.rootCodes.map(codeItemToStoreValue);

        dispatch(new ReceiveDraft(requestId, mapCaseData(draftData.data), connectionTypes, draftData.etag, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDraft(requestId, instanceKey));
      }
    },

  loadEvaluation: (caseNumber: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCase(caseNumber, instanceKey));

      try {

        const {
          bowtieFavouriteId
        } = getBowtieInstanceState(getState, instanceKey);

        const evaluations = await api.loadEvaluation(caseNumber, bowtieFavouriteId);
        const evaluation = evaluations.find(e => e.caseId === caseNumber);

        dispatch(new ReceiveCase(caseNumber, mapChecklistEvaluation(evaluation), instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCase(caseNumber, instanceKey));
      }
    },

  loadConnectedCases: (caseNumber: number, connectionType: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestConnectedCases(caseNumber, connectionType, instanceKey));

      try {

        const {
          bowtieFavouriteId,
          caseDataModel
        } = getBowtieInstanceState(getState, instanceKey);

        const connectedCases = await api.loadConnectedCases([caseNumber], [connectionType], bowtieFavouriteId);

        dispatch(new ReceiveConnectedCases(caseNumber, connectionType,
          filterConnectedCases(caseNumber, connectedCases, caseDataModel), instanceKey
        ));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectedCases(caseNumber, connectionType, instanceKey));
      }
    },

  loadConnectedCasesByConnectionType: (caseNumber: number, connectionType: number[], instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestConnectedCasesByConnectionType(caseNumber, connectionType, instanceKey));
      try {
        const connectedCases = await api.loadConnectedCases([caseNumber], connectionType);
        const groupedByConnectionType = groupConnectedCasesByConnectionType(connectedCases[0]?.connected || []);
        dispatch(new ReceiveConnectedCasesByConnectionType(caseNumber, groupedByConnectionType, instanceKey));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  clean: (instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      clientCache.removeItem(bowtieFavouriteIdCache);
      dispatch(new CleanData(instanceKey));
    },

  addConnection: (draftId: number, caseNumber: number, connectionTypeId: number, instanceKey: string, eTag: string):
    ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber, instanceKey));

        await api.addDraftConnection(draftId, caseNumber, connectionTypeId, eTag);

        dispatch(new ReceiveConnectDraft(caseNumber, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber, instanceKey));
      }

      dispatch(actionCreators.loadDraft(draftId, instanceKey));
    },

  removeConnection: (draftId: number, caseNumber: number, connectionTypeId: number, instanceKey: string, eTag: string): 
  ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber, instanceKey));

        await api.removeDraftConnection(draftId, caseNumber, connectionTypeId, eTag);

        dispatch(new ReceiveConnectDraft(caseNumber, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber, instanceKey));
      }

      dispatch(actionCreators.loadDraft(draftId, instanceKey));
    },

  createBowtieFavouriteIfNeeded: (instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        let bowtieFavouriteId = getBowtieInstanceState(getState, instanceKey)?.bowtieFavouriteId;

        if (!bowtieFavouriteId) {
          const storedId = clientCache.getItem(bowtieFavouriteIdCache);
          bowtieFavouriteId = storedId ? parseInt(storedId, 10) : null;
        }
        if (!bowtieFavouriteId) {
          dispatch(new RequestBowtieFavourite(instanceKey));
          const utcOffset = getState().app.settings.utcOffset;
          bowtieFavouriteId = await api.createBowtieFavourite({ utcOffset });
          clientCache.setItem(bowtieFavouriteIdCache, bowtieFavouriteId?.toString());
        }
        dispatch(new ReceiveBowtieFavourite(bowtieFavouriteId, instanceKey));
      }
      catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  incrementRequestId: (instanceKey: string): ActionDispatching<any, any> =>
    (dispatch: any) => {
      dispatch(new IncrementRequestId(instanceKey));
    },

  updateBowtieInstance: (instanceKey: string, caseId: number, connectionType: number[]): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      try {
        const {
          bowtieFavouriteId,
          currentCaseNumber
        } = getBowtieInstanceState(getState, instanceKey);
        const [bowtieData, connectedCases] = await Promise.all([
          api.loadBowtie(currentCaseNumber, true, null, bowtieFavouriteId),
          connectionType.length ? api.loadConnectedCases([caseId], connectionType, bowtieFavouriteId) : Promise.resolve(null)
        ]);
        const caseDataModel = mapBowtieData(bowtieData.caseDataModel);
        const groupedByConnectionType = connectedCases ?
          groupConnectedCases(filterConnectedCases(caseId, connectedCases, caseDataModel) || []) : undefined;
        dispatch(new UpdateBowtie(
          caseId,
          caseDataModel,
          bowtieData.caseTemplate,
          groupedByConnectionType,
          instanceKey
        ));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  changeSequenceBowtie: ({
    instanceKey,
    parentCaseId,
    caseId,
    siblingCaseIds,
    sequenceOrder,
    allowWarnings,
    steps
  }: {
    instanceKey: string;
    parentCaseId: number;
    caseId: number;
    siblingCaseIds: number[];
    sequenceOrder: number[];
    allowWarnings: boolean;
    steps?: number;
  }): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestChangeConnectionSequence(caseId, parentCaseId, instanceKey));
      const {
          bowtieFavouriteId,
          currentCaseNumber,
          caseDataModel: dataModel
        } = getBowtieInstanceState(getState, instanceKey);
      const moveSteps = calculateMoveSteps<number>(siblingCaseIds, sequenceOrder, caseId, steps);
      const currentIndex = siblingCaseIds.indexOf(caseId);
      if (currentIndex === -1) {
        return;
      }
      try {
        const newSequence = updateSequence<number>(siblingCaseIds, currentIndex, moveSteps);
        const etag = dataModel.casesETags[parentCaseId];
        await caseApi.changeConnectionSequence(
          parentCaseId,
          caseId,
          1,
          newSequence.indexOf(caseId) + 1,
          siblingCaseIds,
          allowWarnings,
          false,
          etag,
          false
          );
        const bowtieData = await api.loadBowtie(currentCaseNumber, true, null, bowtieFavouriteId);
        const caseDataModel = mapBowtieData(bowtieData.caseDataModel);
        dispatch(new UpdateBowtie(
          caseId,
          caseDataModel,
          bowtieData.caseTemplate,
          undefined,
          instanceKey
        ));
        dispatch(new ReceiveChangeConnectionSequence(caseId, parentCaseId, instanceKey));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailChangeConnectionSequence(caseId, parentCaseId, instanceKey));
      }
    },

  validateCaseId: (instanceKey: string, caseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestValidateCaseId(instanceKey, caseId));
        const entityId = await caseApi.caseEntityId(caseId);
        dispatch(new ReceiveValidateCaseId(instanceKey, caseId, entityId));
      } catch {
        dispatch(new FailValidateCaseId(instanceKey, caseId));
      }
    }
};

